import React from 'react';
import { FiUsers, FiTruck, FiPlus, FiTrash2 } from 'react-icons/fi';
import { generateId } from '@/utils/helpers';
import {
  FormSection,
  SectionTitle,
  SubSectionTitle,
  FormGrid,
  FormGroup,
  Label,
  Input,
  CheckboxGroup,
  CheckboxLabel,
  ToggleSwitch,
  ToggleInput,
  ToggleLabel,
  InfoBox,
  HelpText,
  DynamicEntryContainer,
  EntryHeader,
  EntryTitle,
  EntryNumber,
  RemoveButton,
  AddButton
} from '../common/FormStyles';
import { ClientsSuppliers, Supplier } from '@/types/application-forms';
import { Candidate } from '@/models';

interface ClientsSuppliersSectionProps {
  data: Partial<Candidate>;
  onChange: (data: Partial<Candidate>) => void;
  readonly?: boolean;
}

export const ClientsSuppliersSection: React.FC<ClientsSuppliersSectionProps> = ({
  data,
  onChange,
  readonly = false
}) => {
  const handleChange = (field: string, value: any) => {
    onChange({ ...data, [field]: value });
  };

  const handleClientTypeToggle = (type: string, checked: boolean) => {
    const currentTypes = data.types_clients || [];
    const newTypes = checked
      ? [...currentTypes, type as any]
      : currentTypes.filter(t => t !== type);
    handleChange('types_clients', newTypes);
  };

  const handleSupplierChange = (index: number, field: string, value: any) => {
    const newFournisseurs = [...(data.fournisseurs || [])];
    newFournisseurs[index] = { ...newFournisseurs[index], [field]: value };
    handleChange('fournisseurs', newFournisseurs);
  };

  const addSupplier = () => {
    const newSupplier: Supplier = {
      id: generateId(),
      nom: '',
      credit_accorde: false
    };
    handleChange('fournisseurs', [...(data.fournisseurs || []), newSupplier]);
  };

  const removeSupplier = (index: number) => {
    const newFournisseurs = (data.fournisseurs || []).filter((_, i) => i !== index);
    handleChange('fournisseurs', newFournisseurs);
  };

  return (
    <FormSection>
      <SectionTitle>
        <FiUsers />
        Clients et Fournisseurs
      </SectionTitle>

      <InfoBox $type="info">
        <p>
          Les informations sur vos clients et fournisseurs nous aident à comprendre
          la solidité de votre réseau commercial et la stabilité de votre activité.
        </p>
      </InfoBox>

      <SubSectionTitle>Principaux Clients</SubSectionTitle>

      <FormGroup>
        <Label $required>Types de clients</Label>
        <CheckboxGroup>
          <CheckboxLabel>
            <input
              type="checkbox"
              checked={(data.types_clients || []).includes('Particuliers (grand public)')}
              onChange={e => handleClientTypeToggle('Particuliers (grand public)', e.target.checked)}
              disabled={readonly}
            />
            Particuliers (grand public)
          </CheckboxLabel>
          <CheckboxLabel>
            <input
              type="checkbox"
              checked={(data.types_clients || []).includes('Autres commerçants')}
              onChange={e => handleClientTypeToggle('Autres commerçants', e.target.checked)}
              disabled={readonly}
            />
            Autres commerçants
          </CheckboxLabel>
          <CheckboxLabel>
            <input
              type="checkbox"
              checked={(data.types_clients || []).includes('Entreprises')}
              onChange={e => handleClientTypeToggle('Entreprises', e.target.checked)}
              disabled={readonly}
            />
            Entreprises
          </CheckboxLabel>
          <CheckboxLabel>
            <input
              type="checkbox"
              checked={(data.types_clients || []).includes('État/Administration')}
              onChange={e => handleClientTypeToggle('État/Administration', e.target.checked)}
              disabled={readonly}
            />
            État/Administration
          </CheckboxLabel>
          <CheckboxLabel>
            <input
              type="checkbox"
              checked={(data.types_clients || []).includes('ONG/Internationaux')}
              onChange={e => handleClientTypeToggle('ONG/Internationaux', e.target.checked)}
              disabled={readonly}
            />
            ONG/Organisations internationales
          </CheckboxLabel>
        </CheckboxGroup>
        <HelpText>Vous pouvez sélectionner plusieurs types</HelpText>
      </FormGroup>

      {(data.types_clients || []).includes('Entreprises') && (
        <FormGroup>
          <Label>Lesquelles ? (entreprises clientes)</Label>
          <Input
            type="text"
            value={data.entreprises_clientes || ''}
            onChange={e => handleChange('entreprises_clientes', e.target.value)}
            placeholder="Ex: SODECI, MTN, Total..."
            disabled={readonly}
          />
          <HelpText>Séparez les noms par des virgules</HelpText>
        </FormGroup>
      )}

      <FormGroup>
        <Label>Avez-vous des contrats avec vos clients ?</Label>
        <ToggleSwitch>
          <ToggleInput
            checked={data.a_contrats || false}
            onChange={e => handleChange('a_contrats', e.target.checked)}
            disabled={readonly}
          />
          <ToggleLabel>
            {data.a_contrats ? 'Oui, j\'ai des contrats' : 'Non, pas de contrats formels'}
          </ToggleLabel>
        </ToggleSwitch>
      </FormGroup>

      {data.a_contrats && (
        <FormGroup>
          <Label>Nombre de contrats actifs</Label>
          <Input
            type="number"
            min="0"
            value={data.nombre_contrats || ''}
            onChange={e => handleChange('nombre_contrats', parseInt(e.target.value))}
            placeholder="Ex: 3"
            disabled={readonly}
          />
          <HelpText>Nombre de contrats de fourniture ou de service en cours</HelpText>
        </FormGroup>
      )}

      <SubSectionTitle>
        <FiTruck style={{ fontSize: '18px' }} />
        Principaux Fournisseurs
      </SubSectionTitle>

      <InfoBox $type="warning">
        <p>
          Ajoutez vos principaux fournisseurs. Ces informations peuvent être vérifiées
          pour confirmer la régularité de votre activité commerciale.
        </p>
      </InfoBox>

      {(data.fournisseurs || []).map((fournisseur, index) => (
        <DynamicEntryContainer key={fournisseur.id}>
          <EntryHeader>
            <EntryTitle>
              <EntryNumber>{index + 1}</EntryNumber>
              Fournisseur {index + 1}
            </EntryTitle>
            <RemoveButton
              onClick={() => removeSupplier(index)}
              disabled={readonly || (data.fournisseurs || []).length <= 1}
            >
              <FiTrash2 />
              Supprimer
            </RemoveButton>
          </EntryHeader>

          <FormGroup>
            <Label $required>Nom du fournisseur</Label>
            <Input
              type="text"
              value={fournisseur.nom || ''}
              onChange={e => handleSupplierChange(index, 'nom', e.target.value)}
              placeholder="Ex: Établissement Kouassi, Marché de Gros..."
              disabled={readonly}
            />
          </FormGroup>

          <FormGroup>
            <Label>Le fournisseur vous accorde-t-il un crédit ?</Label>
            <ToggleSwitch>
              <ToggleInput
                checked={fournisseur.credit_accorde || false}
                onChange={e => handleSupplierChange(index, 'credit_accorde', e.target.checked)}
                disabled={readonly}
              />
              <ToggleLabel>
                {fournisseur.credit_accorde ? 'Oui, crédit accordé' : 'Non, paiement comptant'}
              </ToggleLabel>
            </ToggleSwitch>
          </FormGroup>

          {fournisseur.credit_accorde && (
            <FormGroup>
              <Label>Délai de crédit (en jours)</Label>
              <Input
                type="number"
                min="0"
                value={fournisseur.delai_credit_jours || ''}
                onChange={e => handleSupplierChange(index, 'delai_credit_jours', parseInt(e.target.value))}
                placeholder="Ex: 30"
                disabled={readonly}
              />
              <HelpText>Nombre de jours de crédit accordé</HelpText>
            </FormGroup>
          )}
        </DynamicEntryContainer>
      ))}

      {!readonly && (
        <AddButton onClick={addSupplier}>
          <FiPlus />
          Ajouter un fournisseur
        </AddButton>
      )}

      {(data.fournisseurs || []).length === 0 && (
        <InfoBox $type="warning">
          <p>
            Ajoutez au moins un fournisseur principal. Cette information est importante
            pour démontrer la régularité de votre activité.
          </p>
        </InfoBox>
      )}
    </FormSection>
  );
};
